/* Common Imports*/

import { styled } from "@mui/system";
import React from "react";

/* Component Imports */

import { Popover, Skeleton, Typography, useTheme } from "@mui/material";

/* Styling */

const MainContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	height: "5rem",
	width: "100%",
	overflow: "wrap",
	padding: "0.75rem 1rem 0.75rem 1rem",
	background: "#FFF",
	borderRadius: "1rem",
	border: "1px solid rgba(0, 0, 0, 0.12)",
	gap: "0.45rem",
	/* XS breakpoint */
	[theme.breakpoints.up("xs")]: {
		width: "46.5%",
	},
	/*iphone se - 375*/
	[theme.breakpoints.up(theme.breakpoints.values.xs + 94)]: {
		width: "47.5%",
	},
	/*iphone XR - 410*/
	[theme.breakpoints.up(theme.breakpoints.values.xs + 130)]: {
		width: "47.5%",
	},
	[theme.breakpoints.up("xsPlus")]: {
		width: "48.2%",
	},
	[theme.breakpoints.up("sm")]: {
		width: "48.2%",
		height: "5.5rem",
	},
	/*ipad Mini */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 167)]: {
		width: "48.7%",
	},
	/* ipad Air*/
	[theme.breakpoints.up(theme.breakpoints.values.sm + 219)]: {
		width: "23.2%",
	},
	[theme.breakpoints.up("md")]: {
		width: "48.57%",
	},
	/* MDLG Breakpoint iPadPro*/
	[theme.breakpoints.up(theme.breakpoints.values.md + 64)]: {
		width: "48.65%",
	},
	/*720p and 768p breakpoint */
	[theme.breakpoints.up("lg")]: {
		// height: "5.75rem",
		width: "23.5%",
	},
	/* 1080p 125% breakpoint*/
	[theme.breakpoints.up(theme.breakpoints.values.lg + 150)]: {
		width: "23.76%",
	},
	/* 1080p breakpoint*/
	[theme.breakpoints.up("xl")]: {
		width: "23.54%",
	},
	/* XXL breakpoint  2560p*/
	[theme.breakpoints.up(theme.breakpoints.values.xl + 640)]: {
		width: "23.79%",
	},
	/*4k breakpoint 3840p*/
	[theme.breakpoints.up(theme.breakpoints.values.xl + 1920)]: {
		width: "23.89%",
	},
}));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
}));

const CustomPopover = styled(Popover)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "flex-start",
	width: "100%",
	height: "100%",
	"& .MuiPopover-paper": {
		padding: "1rem 1rem 1rem 1rem",
		borderRadius: "16px",
		overflow: "wrap",
		boxShadow: "0px 4px 18px 0px rgba(0, 0, 0, 0.05)",
	},
}));

const Value = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	fontWeight: "400",
	lineHeight: "1.25rem",
	[theme.breakpoints.down("sm")]: {
		fontSize: "0.75rem",
		lineHeight: "0.9rem",
	},
}));

const Title = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
	fontWeight: "400",
	color: theme.palette.text.secondary,
	[theme.breakpoints.down("sm")]: {
		fontSize: "0.675rem",
	},
}));

const PropertyHighlightItem = ({
	loading,
	title,
	icon,
	value,
}: {
	loading: boolean;
	title: string | undefined;
	icon: any;
	value: string | string[] | React.ReactNode | undefined;
}) => {
	const theme = useTheme();

	const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
	const open = Boolean(anchorEl);
	const [popoverDisplay, setPopoverDisplay] = React.useState("none");
	const [popoverWidth, setPopoverWidth] = React.useState<number | undefined>(undefined);
	const id = open ? "simple-popover" : undefined;

	const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
		setPopoverDisplay("flex");
		setAnchorEl(event.currentTarget);
		setPopoverWidth(event.currentTarget.getBoundingClientRect().width);
	};

	const handleClose = () => {
		setPopoverDisplay("none");
		setAnchorEl(null);
		setPopoverWidth(undefined);
	};

	React.useEffect(() => {
		if (open) {
			const handleScroll = () => {
				if (open) {
					setPopoverDisplay("none");
					handleClose();
				}
			};
			window.addEventListener("scroll", handleScroll);
			return () => {
				window.removeEventListener("scroll", handleScroll);
			};
		}
	}, [open]);

	if (loading) {
		return (
			<MainContainer>
				<ContentContainer>
					<Skeleton
						animation="wave"
						variant="text"
						width="6rem"
					/>
					<Skeleton
						animation="wave"
						variant="rectangular"
						width="1.5rem"
						height="1.5rem"
						sx={{
							borderRadius: "8px",
						}}
					/>
				</ContentContainer>
				<Skeleton
					animation="wave"
					variant="text"
					width="4rem"
				/>
			</MainContainer>
		);
	}

	return (
		<React.Fragment>
			{/* Popover */}

			<CustomPopover
				id={id}
				disableScrollLock
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				sx={{
					display: popoverDisplay,
				}}
				anchorOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				slotProps={{
					paper: {
						style: {
							width: popoverWidth,
						},
					},
				}}
			>
				<ContentContainer
					sx={{
						margin: "0rem 0rem 0.75rem 0rem",
					}}
				>
					<Typography variant="body1">{value ? value : ""}</Typography>
					{icon}
				</ContentContainer>
				<Typography
					variant="body2"
					color={theme.palette.text.secondary}
				>
					{title}
				</Typography>
			</CustomPopover>

			{/* Card */}

			<MainContainer
				aria-describedby={id}
				onClick={typeof value === "string" && value.length > 23 ? handleClick : undefined}
				sx={{
					cursor: typeof value === "string" && value.length > 23 ? "pointer" : "default",
				}}
			>
				<ContentContainer>
					<Value variant="body1">
						{value
							? typeof value === "string"
								? value.length > 23
									? value.substring(0, 23) + "..."
									: value
								: Array.isArray(value)
									? value.length > 1
										? value.join(", ")
										: value[0]
									: value
							: ""}
					</Value>
					{icon}
				</ContentContainer>
				<Title variant="body2">{title ? title : ""}</Title>
			</MainContainer>
		</React.Fragment>
	);
};

export default PropertyHighlightItem;
